/**
 * cart-lock-manager.js
 * Granular CRM lock/unlock API for cart coupons and points.
 *
 * Exposed as: window.CartLockManager
 *
 * CartCoupon and CartPoints each call only their own targeted methods.
 * Coupon operations never touch points and vice versa.
 */

(function (global) {
  'use strict';

  var INTEGRATION_URL  = '/apps/integration';
  var STOREFRONT_URL   = '/api/2024-10/graphql.json';

  // Storefront Access Token — read from theme setting via [data-storefront-token]
  // (rendered on #cart-order-summary). Falls back to null if the element is absent.
  function _storefrontToken() {
    var el = document.querySelector('[data-storefront-token]');
    return el ? (el.getAttribute('data-storefront-token') || null) : null;
  }

  var _customerId   = null;
  var _membershipId = null;

  // Stored after a successful lock-member-point so unlockPoints can send them back.
  // Also seeded from get-member-point-voucher-timer on page-load restore.
  var _lockedPointsAmount = null;   // integer, e.g. 200
  var _lockedPointsCode   = null;   // discountCode returned by lock-member-point

  function init(customerId, membershipId) {
    _customerId   = customerId;
    _membershipId = membershipId;
  }

  /**
   * Seed the stored points-lock values from an external source (e.g. page-load
   * restore from get-member-point-voucher-timer). Call this before the user
   * can interact with the remove-points button so unlockPoints has the right payload.
   */
  function seedPointsLock(amount, discountCode) {
    _lockedPointsAmount = amount   || null;
    _lockedPointsCode   = discountCode || null;
  }

  async function _post(payload) {
    var body = new FormData();
    Object.keys(payload).forEach(function (k) { body.append(k, payload[k]); });
    try {
      var res  = await fetch(INTEGRATION_URL, { method: 'POST', body: body });
      var text = await res.text();
      if (!res.ok) { console.error('[CartLockManager] HTTP', res.status, payload.action); return null; }
      if (!text.trim()) return { status: 'success' };
      try { return JSON.parse(text); } catch (e) { return null; }
    } catch (err) {
      console.error('[CartLockManager] Fetch error:', err);
      return null;
    }
  }

  /* ─── Manual (non-CRM) discount codes ────────────────────────────────────── */
  // Plain Shopify discount codes the shopper typed into the coupon drawer. The
  // CRM never sees them, so they are missing from get-member-point-voucher-timer's
  // coupon_codes. Because cartDiscountCodesUpdate is a FULL replacement, syncing
  // the CRM list verbatim would silently drop them — union them in instead.
  function _manualCodes() {
    return (global.CartCoupon && typeof global.CartCoupon.getManualCodes === 'function')
      ? global.CartCoupon.getManualCodes()
      : [];
  }

  function _isManualCode(code) {
    return _manualCodes().indexOf(code) !== -1;
  }

  /* ─── Storefront API ─────────────────────────────────────────────────────── */

  // cartDiscountCodesUpdate: full replacement — send the exact list you want active.
  // Sending [] removes all discount codes. Shopify validates each code server-side.
  var _discountMutation = [
    'mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {',
    '  cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {',
    '    cart { id discountCodes { code applicable } }',
    '    userErrors { field message }',
    '  }',
    '}',
  ].join('\n');

  /**
   * Sync Shopify cart discount codes to exactly the given list.
   * Derives the Storefront cartId from the AJAX cart token (/cart.js).
   * The AJAX cart token IS the Storefront Cart ID — same underlying object.
   *
   * Called automatically from fetchTimer() after every CRM lock/unlock.
   * Also exposed publicly so cart-points.js _init() can call it on page-load restore.
   *
   * Manually-typed plain Shopify discount codes are unioned in here rather than at
   * the call sites: cart-points.js _init() calls this directly (bypassing
   * fetchTimer) three times on every cart-page boot — including
   * syncShopifyDiscounts([]) when there is no active CRM lock — and a full
   * replacement built from CRM data alone would drop them on every page load.
   */
  async function syncShopifyDiscounts(codes) {
    var notApplicableFound = [];
    if (global.CartCoupon && typeof global.CartCoupon.whenReady === 'function') {
      await global.CartCoupon.whenReady();
    }
    codes = (codes || []).slice();
    _manualCodes().forEach(function (c) { if (codes.indexOf(c) === -1) codes.push(c); });
    try {
      var cartRes = await fetch('/cart.js');
      if (!cartRes.ok) return notApplicableFound;
      var cart = await cartRes.json();
      if (!cart.token) return notApplicableFound;
      var cartId = 'gid://shopify/Cart/' + cart.token;
      console.log('[CartLockManager] → cartDiscountCodesUpdate', codes);
      var sfRes = await fetch(STOREFRONT_URL, {
        method:  'POST',
        headers: {
          'Content-Type':                      'application/json',
          'X-Shopify-Storefront-Access-Token': _storefrontToken(),
        },
        body: JSON.stringify({
          query:     _discountMutation,
          variables: { cartId: cartId, discountCodes: codes },
        }),
      });
      var sfData = await sfRes.json();
      console.log('[CartLockManager] ← cartDiscountCodesUpdate', sfData);
      var update = sfData.data && sfData.data.cartDiscountCodesUpdate;
      if (update && update.userErrors && update.userErrors.length > 0) {
        console.error('[CartLockManager] syncShopifyDiscounts errors:', update.userErrors);
      }

      // Unlock any coupon codes Shopify reports as not applicable, then re-sync.
      // POINTS-xxx codes are excluded — they go through unlockPoints() instead.
      // Returns the not-applicable codes so fetchTimer() can strip them from its
      // response before handing back to _syncFromTimer / restoreFromCodes.
      if (update && update.cart && update.cart.discountCodes) {
        var notApplicable = update.cart.discountCodes
          .filter(function (dc) { return !dc.applicable && dc.code.indexOf('POINTS') !== 0; })
          .map(function (dc) { return dc.code; });

        if (notApplicable.length > 0) {
          notApplicableFound = notApplicable;
          console.warn('[CartLockManager] Non-applicable coupon codes detected, unlocking:', notApplicable);
          // Manual (non-CRM) codes have no member-coupon record — deregister them
          // with CartCoupon instead of calling unlock-member-coupon, which would
          // fail and log a spurious error.
          var crmNotApplicable = notApplicable.filter(function (c) {
            if (_isManualCode(c)) { global.CartCoupon.dropManualCode(c); return false; }
            return true;
          });
          if (crmNotApplicable.length > 0) await unlockCoupons(crmNotApplicable);
          var validCodes = codes.filter(function (c) { return notApplicable.indexOf(c) === -1; });
          await syncShopifyDiscounts(validCodes);
        }
      }
    } catch (err) {
      console.error('[CartLockManager] syncShopifyDiscounts failed:', err);
    }
    return notApplicableFound;
  }

  /** Lock a single coupon code. Returns true on success. */
  async function lockCoupon(code) {
    if (!_customerId) return false;
    console.log('[CartLockManager] → lock-member-coupon', code);
    var res = await _post({ action: 'lock-member-coupon', couponNo: code, customerId: _customerId });
    console.log('[CartLockManager] ← lock-member-coupon', res);
    return !!(res && res.status === 'success');
  }

  /** Unlock one or more coupon codes in a single request. */
  async function unlockCoupons(codes) {
    if (!_customerId || !codes || codes.length === 0) return;
    console.log('[CartLockManager] → unlock-member-coupon', codes);
    var body = new FormData();
    body.append('action', 'unlock-member-coupon');
    body.append('customerId', _customerId);
    codes.forEach(function (code) { body.append('couponNo[]', code); });
    try {
      var res  = await fetch(INTEGRATION_URL, { method: 'POST', body: body });
      var text = await res.text();
      console.log('[CartLockManager] ← unlock-member-coupon', res.status, text);
      if (!res.ok) console.error('[CartLockManager] unlock-member-coupon failed');
    } catch (err) {
      console.error('[CartLockManager] Fetch error (unlock coupons):', err);
    }
  }

  /**
   * Lock a points tier.
   * Saves discountCode + points amount from the response for use in unlockPoints.
   * Returns true on success.
   */
  async function lockPoints(tier) {
    if (!_customerId || !_membershipId) return false;
    console.log('[CartLockManager] → lock-member-point', tier.points);
    var res = await _post({
      action:     'lock-member-point',
      oldCode:    _membershipId,
      customerId: _customerId,
      points:     tier.points,
    });
    console.log('[CartLockManager] ← lock-member-point', res);
    if (res && res.status === 'success') {
      _lockedPointsAmount = tier.points;
      _lockedPointsCode   = res.discountCode || null;
    }
    return !!(res && res.status === 'success');
  }

  /**
   * Unlock currently locked points.
   * Sends back the points amount and generated discount code that were stored
   * when lockPoints (or seedPointsLock) was last called.
   */
  async function unlockPoints() {
    if (!_customerId || !_membershipId) return;
    console.log('[CartLockManager] → unlock-member-point', {
      points: _lockedPointsAmount,
      couponCodes: _lockedPointsCode,
    });
    var body = new FormData();
    body.append('action',     'unlock-member-point');
    body.append('oldCode',    _membershipId);
    body.append('customerId', _customerId);
    if (_lockedPointsAmount !== null) body.append('points',        _lockedPointsAmount);
    if (_lockedPointsCode)            body.append('couponCodes[]', _lockedPointsCode);
    try {
      var res  = await fetch(INTEGRATION_URL, { method: 'POST', body: body });
      var text = await res.text();
      console.log('[CartLockManager] ← unlock-member-point', res.status, text);
      if (!res.ok) console.error('[CartLockManager] unlock-member-point failed');
    } catch (err) {
      console.error('[CartLockManager] Fetch error (unlock points):', err);
    }
    _lockedPointsAmount = null;
    _lockedPointsCode   = null;
  }

  /**
   * Fetch timer from server, hand off to CartTimer, and return the full response.
   * Callers read res.data.coupon_codes / res.data.points to sync UI after any
   * operation that may cause backend auto-lock/unlock side-effects.
   */
  async function fetchTimer() {
    if (!_customerId) return null;
    console.log('[CartLockManager] → get-member-point-voucher-timer');
    var res = await _post({ action: 'get-member-point-voucher-timer', customerId: _customerId });
    console.log('[CartLockManager] ← get-member-point-voucher-timer', res);
    if (global.CartTimer) {
      var timeApplied = res && res.status === 'success' && res.data && res.data.time_applied;
      global.CartTimer.startAt(timeApplied || null);
    }
    // Always sync Shopify cart discounts to the server's authoritative coupon list.
    // This covers both apply (new codes added) and remove (codes cleared).
    // syncShopifyDiscounts unions in manually-typed codes itself — the CRM list is
    // authoritative for member coupons only, and this sync is a full replacement.
    var codes = (res && res.status === 'success' && res.data && res.data.coupon_codes) || [];
    var notApplicable = await syncShopifyDiscounts(codes);
    // Strip codes Shopify rejected so _syncFromTimer / restoreFromCodes
    // does not re-render tags for coupons that were just unlocked.
    if (notApplicable.length > 0 && res && res.data && res.data.coupon_codes) {
      res.data.coupon_codes = res.data.coupon_codes.filter(function (c) {
        return notApplicable.indexOf(c) === -1;
      });
    }
    return res;
  }

  global.CartLockManager = {
    init:                 init,
    seedPointsLock:       seedPointsLock,
    lockCoupon:           lockCoupon,
    unlockCoupons:        unlockCoupons,
    lockPoints:           lockPoints,
    unlockPoints:         unlockPoints,
    fetchTimer:           fetchTimer,
    syncShopifyDiscounts: syncShopifyDiscounts,
  };

})(window);
